import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { inquiryService } from '../../services/inquiryService';

const LoanRequestDialog = ({ children }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    loan_type: ''
  });
  const [submitStatus, setSubmitStatus] = useState(null);
  const [isOpen, setIsOpen] = useState(false);

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [id]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus('submitting');
    try {
      await inquiryService.createLoanRequest(formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', loan_type: '' });
      setTimeout(() => {
        setSubmitStatus(null);
        setIsOpen(false);
      }, 1000); 
    } catch (error) {
      console.error('Failed to submit loan request:', error);
      setSubmitStatus('error');
      setTimeout(() => setSubmitStatus(null), 3000);
    }
  };

  return (
    <Dialog.Root open={isOpen} onOpenChange={setIsOpen}>
      <Dialog.Trigger asChild>
        {children}
      </Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="bg-black/50 data-[state=open]:animate-overlayShow fixed inset-0 z-50 transition-opacity duration-300" />
        <Dialog.Content className="data-[state=open]:animate-contentShow fixed top-[50%] left-[50%] max-h-[90vh] w-[90vw] max-w-[500px] translate-x-[-50%] translate-y-[-50%] rounded-xl bg-white p-6 shadow-xl ring-1 ring-black/10 focus:outline-none z-50 flex flex-col">
          {/* Header and Close Button */}
          <div className="flex items-center justify-between pb-4 border-b border-gray-200 mb-6">
            <Dialog.Title className="text-2xl font-bold text-gray-900">
              Request Assistance
            </Dialog.Title>
            <Dialog.Close asChild>
              <button
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </Dialog.Close>
          </div>

          <Dialog.Description className="text-gray-600 text-base leading-relaxed mb-6">
            Fill out the form below, and one of our financial experts will get in touch with you to help with your loan queries.
          </Dialog.Description>

          {/* Form Fields */}
          <form className="space-y-4" onSubmit={handleSubmit}>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="name">
                Full Name
              </label>
              <input
                className="block w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2 text-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-accent placeholder-gray-400"
                id="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="e.g., Jane Doe"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="email">
                Email Address
              </label>
              <input
                className="block w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2 text-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-accent placeholder-gray-400"
                id="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="phone">
                Phone Number
              </label>
              <input
                className="block w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2 text-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-accent placeholder-gray-400"
                id="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="e.g., +91 98765 43210"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1" htmlFor="loan_type">
                Loan Type
              </label>
              <select
                className="block w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-2 text-gray-800 transition-colors focus:outline-none focus:ring-2 focus:ring-accent"
                id="loan_type"
                value={formData.loan_type}
                onChange={handleChange}
                required
              >
                <option value="" disabled>Select a loan type...</option>
                <option value="Home Loan">Home Loan</option>
              </select>
            </div>
            <div className="mt-8 flex justify-end items-center">
              {submitStatus === 'submitting' && (
                <div className="flex items-center space-x-2 text-gray-600 mr-4">
                  <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-primary-500"></div>
                  <span>Submitting...</span>
                </div>
              )}
              {submitStatus === 'success' && (
                <span className="text-green-600 mr-4">Submitted successfully!</span>
              )}
              {submitStatus === 'error' && (
                <span className="text-red-600 mr-4">Submission failed.</span>
              )}
              <button
                type="submit"
                className="bg-accent hover:bg-primary-700 text-white font-semibold py-2 px-6 rounded-full transition-all shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={submitStatus === 'submitting'}
              >
                Submit Request
              </button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default LoanRequestDialog;